export function formatDate(date: Date | number | string, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  const hour = d.getHours()
  const minute = d.getMinutes()
  const second = d.getSeconds()
  const millisecond = d.getMilliseconds()
  const weekDay = d.getDay()

  const weekDays = ['日', '一', '二', '三', '四', '五', '六']

  return format
    .replace(/YYYY/g, year.toString())
    .replace(/MM/g, month.toString().padStart(2, '0'))
    .replace(/DD/g, day.toString().padStart(2, '0'))
    .replace(/HH/g, hour.toString().padStart(2, '0'))
    .replace(/mm/g, minute.toString().padStart(2, '0'))
    .replace(/ss/g, second.toString().padStart(2, '0'))
    .replace(/SSS/g, millisecond.toString().padStart(3, '0'))
    .replace(/M/g, month.toString())
    .replace(/D/g, day.toString())
    .replace(/H/g, hour.toString())
    .replace(/d/g, weekDay.toString())
    .replace(/ddd/g, weekDays[weekDay])
}

export function parseDate(dateStr: string, format: string = 'YYYY-MM-DD HH:mm:ss'): Date {
  const year = parseInt(dateStr.substring(format.indexOf('YYYY'), format.indexOf('YYYY') + 4)) || new Date().getFullYear()
  const month = parseInt(dateStr.substring(format.indexOf('MM'), format.indexOf('MM') + 2)) - 1 || 0
  const day = parseInt(dateStr.substring(format.indexOf('DD'), format.indexOf('DD') + 2)) || 1
  const hour = parseInt(dateStr.substring(format.indexOf('HH'), format.indexOf('HH') + 2)) || 0
  const minute = parseInt(dateStr.substring(format.indexOf('mm'), format.indexOf('mm') + 2)) || 0
  const second = parseInt(dateStr.substring(format.indexOf('ss'), format.indexOf('ss') + 2)) || 0

  return new Date(year, month, day, hour, minute, second)
}

export function getRelativeTime(date: Date | number | string): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (seconds < 60) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 30) return `${days}天前`
  if (months < 12) return `${months}个月前`
  return `${years}年前`
}

export function isToday(date: Date | number | string): boolean {
  const d = new Date(date)
  const today = new Date()
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  )
}

export function isYesterday(date: Date | number | string): boolean {
  const d = new Date(date)
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  )
}

export function isTomorrow(date: Date | number | string): boolean {
  const d = new Date(date)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return (
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate()
  )
}

export function isSameDay(date1: Date | number | string, date2: Date | number | string): boolean {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month - 1, 1).getDay()
}

export function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDay()
}

export function addDays(date: Date | number | string, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export function addMonths(date: Date | number | string, months: number): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

export function addYears(date: Date | number | string, years: number): Date {
  const result = new Date(date)
  result.setFullYear(result.getFullYear() + years)
  return result
}

export function getDayOfWeek(date: Date | number | string): string {
  const days = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
  return days[new Date(date).getDay()]
}

export function getWeekOfYear(date: Date | number | string): number {
  const d = new Date(date)
  const start = new Date(d.getFullYear(), 0, 1)
  const diff = d.getTime() - start.getTime()
  const oneWeek = 1000 * 60 * 60 * 24 * 7
  return Math.ceil(diff / oneWeek)
}

export function getAge(birthDate: Date | number | string): number {
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

export function getTimeDiff(date1: Date | number | string, date2: Date | number | string): {
  days: number
  hours: number
  minutes: number
  seconds: number
} {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  let diff = Math.abs(d2.getTime() - d1.getTime())

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  diff %= 1000 * 60 * 60 * 24
  const hours = Math.floor(diff / (1000 * 60 * 60))
  diff %= 1000 * 60 * 60
  const minutes = Math.floor(diff / (1000 * 60))
  diff %= 1000 * 60
  const seconds = Math.floor(diff / 1000)

  return { days, hours, minutes, seconds }
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function getTimestamp(): number {
  return Date.now()
}

export function getUnixTimestamp(): number {
  return Math.floor(Date.now() / 1000)
}

export function fromTimestamp(timestamp: number): Date {
  return new Date(timestamp * 1000)
}
